import { KERB_LANE, LANE_WIDTH } from '@accra/shared';
import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { canvasTexture } from '../../render/materials/canvasTexture';
import { laneX } from '../../systems/lanes';
import { STOP_BOX_HALF, stopAt } from '../../systems/passengers/stopSequence';
import { LAYOUT } from './roadLayout';

function boxTexture() {
  return canvasTexture(128, 512, (ctx) => {
    ctx.strokeStyle = '#ffd400';
    ctx.lineWidth = 10;
    ctx.strokeRect(6, 6, 116, 500);
    ctx.lineWidth = 6;
    for (let y = -128; y < 512; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(128, y + 128);
      ctx.stroke();
    }
    ctx.save();
    ctx.translate(64, 256);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = '#ffd400';
    ctx.font = 'bold 34px Impact, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('BUS STOP', 0, 12);
    ctx.restore();
  });
}

function nameTexture(name: string) {
  return canvasTexture(512, 128, (ctx) => {
    ctx.fillStyle = '#0b6e4f';
    ctx.fillRect(0, 0, 512, 128);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 56px "Arial Black", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.toUpperCase(), 256, 66, 490);
  });
}

class Marker {
  readonly group = new Group();
  private readonly nameMaterial = new MeshStandardMaterial();
  private seq = -1;

  constructor(boxMaterial: MeshStandardMaterial) {
    const box = new Mesh(new PlaneGeometry(LANE_WIDTH - 0.3, STOP_BOX_HALF * 2), boxMaterial);
    box.rotation.x = -Math.PI / 2;
    box.position.set(laneX(KERB_LANE), 0.02, 0);
    const pole = new Mesh(new CylinderGeometry(0.06, 0.06, 3), flatMaterial('#6c757d'));
    pole.position.set(LAYOUT.sidewalkX - 1.2, 1.6, -STOP_BOX_HALF + 1);
    const board = new Mesh(new PlaneGeometry(2.6, 0.65), this.nameMaterial);
    board.position.set(LAYOUT.sidewalkX - 1.2, 3, -STOP_BOX_HALF + 1);
    board.rotation.y = -Math.PI / 2.6;
    const roof = new Mesh(new BoxGeometry(2.2, 0.15, 6), flatMaterial('#2b9348'));
    roof.position.set(LAYOUT.sidewalkX + 0.4, 2.7, 0);
    roof.castShadow = true;
    this.group.add(box, pole, board, roof);
  }

  invalidate(): void {
    this.seq = -1;
  }

  show(seq: number): void {
    if (seq === this.seq) return;
    this.seq = seq;
    const point = stopAt(seq);
    this.group.position.z = -point.position;
    this.nameMaterial.map?.dispose();
    this.nameMaterial.map = nameTexture(point.stop.name);
    this.nameMaterial.needsUpdate = true;
  }
}

/** Yellow boxes, shelters and name boards for the current and next stop. */
export class StopMarkers {
  readonly group = new Group();
  private readonly markers: Marker[];

  constructor() {
    const boxMaterial = new MeshStandardMaterial({ map: boxTexture(), transparent: true });
    this.markers = [new Marker(boxMaterial), new Marker(boxMaterial)];
    this.markers.forEach((m) => this.group.add(m.group));
  }

  invalidate(): void {
    this.markers.forEach((m) => m.invalidate());
  }

  update(currentSeq: number): void {
    this.markers[currentSeq % 2]?.show(currentSeq);
    this.markers[(currentSeq + 1) % 2]?.show(currentSeq + 1);
  }
}
